import { useEffect, useMemo, useState } from 'react'
import MapPanel from './hotel-map/MapPanel'
import SearchPanel from './hotel-map/SearchPanel'
import Filters from './hotel-map/Filters'
import HotelCard from './hotel-map/HotelCard'
import { applyFilters, hotelsInBounds, sortHotels, uniqueSorted, PAGE_SIZE } from './hotel-map/hotelUtils'
import './HotelCreditsMap.css'

export default function HotelCreditsMap() {
  const [payload, setPayload] = useState(null)
  const [error, setError] = useState('')
  const [view, setView] = useState('map')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)
  const [inEdit, setInEdit] = useState(false)
  const [selectBrand, setSelectBrand] = useState(false)
  const [chain, setChain] = useState('')
  const [brand, setBrand] = useState('')
  const [country, setCountry] = useState('')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState('name')
  const [bounds, setBounds] = useState(null)
  const [selected, setSelected] = useState(null)
  const [page, setPage] = useState(1)

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ''}/data/hotels.json`)
      .then((res) => {
        if (!res.ok) throw new Error('Could not load hotel data')
        return res.json()
      })
      .then(setPayload)
      .catch((err) => setError(err.message))
  }, [])

  const hotels = useMemo(() => payload?.hotels || [], [payload])

  const filtered = useMemo(
    () => applyFilters(hotels, { inEdit, selectBrand, chain, brand, country, query }),
    [hotels, inEdit, selectBrand, chain, brand, country, query],
  )

  const sorted = useMemo(() => sortHotels(filtered, sort), [filtered, sort])

  const inView = useMemo(
    () => (view === 'map' ? hotelsInBounds(sorted, bounds) : sorted),
    [sorted, bounds, view],
  )

  const visiblePage = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(inView.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages)
    return inView.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  }, [inView, page])

  const chains = useMemo(() => uniqueSorted(hotels.map((h) => h.chain)), [hotels])
  const brands = useMemo(
    () => uniqueSorted(hotels.filter((h) => !chain || h.chain === chain).map((h) => h.brand)),
    [hotels, chain],
  )
  const countries = useMemo(() => uniqueSorted(hotels.map((h) => h.country)), [hotels])

  const filterCount = [inEdit, selectBrand, chain, brand, country].filter(Boolean).length

  useEffect(() => {
    setPage(1)
  }, [inEdit, selectBrand, chain, brand, country, query, sort, view])

  const updateFilters = (next) => {
    if ('inEdit' in next) setInEdit(next.inEdit)
    if ('selectBrand' in next) setSelectBrand(next.selectBrand)
    if ('chain' in next) setChain(next.chain)
    if ('brand' in next) setBrand(next.brand)
    if ('country' in next) setCountry(next.country)
  }

  return (
    <div className="hotelMapPage">
      <div className="hotelMapChrome">
        <div className="hotelMapIntro">
          <h1>Sapphire Hotel Credits</h1>
          <p>
            Every hotel in The Edit, plus every property in the chains that qualify for the $250 Select Brand credit.
            Book prepaid 2-night stays through Chase Travel and confirm eligibility in the portal.
          </p>
        </div>

        <div className="hotelToolbar">
          <div className="hotelTabs" role="tablist">
            <button type="button" className={view === 'map' ? 'isActive' : ''} onClick={() => setView('map')}>
              Map View
            </button>
            <button type="button" className={view === 'search' ? 'isActive' : ''} onClick={() => setView('search')}>
              Search View
            </button>
          </div>
          <button
            type="button"
            className={`hotelToolBtn ${filtersOpen ? 'isActive' : ''}`}
            onClick={() => { setFiltersOpen((v) => !v); setSortOpen(false) }}
          >
            Filters{filterCount ? <span className="hotelBadge">{filterCount}</span> : null}
          </button>
          <button
            type="button"
            className={`hotelToolBtn ${sortOpen ? 'isActive' : ''}`}
            onClick={() => { setSortOpen((v) => !v); setFiltersOpen(false) }}
          >
            Sort
          </button>
          <input
            className="hotelSearchInput"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search hotels, cities, brands"
          />
        </div>

        {filtersOpen ? (
          <Filters
            inEdit={inEdit}
            selectBrand={selectBrand}
            chain={chain}
            brand={brand}
            country={country}
            chains={chains}
            brands={brands}
            countries={countries}
            onChange={updateFilters}
            onClose={() => setFiltersOpen(false)}
          />
        ) : null}

        {sortOpen ? (
          <div className="hotelSortMenu">
            {[
              ['name', 'Name'],
              ['city', 'Location'],
              ['double-dip', 'Double-dip first'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={sort === value ? 'isActive' : ''}
                onClick={() => { setSort(value); setSortOpen(false) }}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="hotelLegend">
          <span><i className="dot both" /> Double-dip</span>
          <span><i className="dot edit" /> The Edit</span>
          <span><i className="dot select" /> Select Brand</span>
        </div>
        {payload?.counts ? (
          <p className="hotelCounts">
            {payload.counts.total.toLocaleString()} hotels · {payload.counts.inEdit.toLocaleString()} Edit · {payload.counts.selectBrand.toLocaleString()} Select Brand · {payload.counts.doubleDip.toLocaleString()} overlap
          </p>
        ) : null}
      </div>

      {error ? <p className="hotelError">{error}. Run <code>npm run build:hotels</code> to generate the dataset.</p> : null}

      {view === 'map' ? (
        <div className="hotelMapLayout">
          <div className="hotelMapCanvas">
            <MapPanel
              hotels={sorted}
              selected={selected}
              onSelect={setSelected}
              onBoundsChange={setBounds}
            />
          </div>
          <aside className="hotelSidebar">
            <h2>Properties in view</h2>
            <p className="hotelSidebarCount">{inView.length.toLocaleString()}</p>
            <div className="hotelSidebarList">
              {visiblePage.map((hotel) => (
                <HotelCard
                  key={hotel.id}
                  hotel={hotel}
                  selected={selected?.id === hotel.id}
                  onSelect={setSelected}
                />
              ))}
              {!visiblePage.length ? <p className="hotelEmpty">No properties in view</p> : null}
            </div>
            {inView.length > PAGE_SIZE ? (
              <div className="hotelPager">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                <span>{page} / {Math.max(1, Math.ceil(inView.length / PAGE_SIZE))}</span>
                <button
                  type="button"
                  disabled={page >= Math.ceil(inView.length / PAGE_SIZE)}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </aside>
        </div>
      ) : (
        <SearchPanel
          hotels={sorted}
          page={page}
          selected={selected}
          onSelect={setSelected}
          onPage={setPage}
        />
      )}
    </div>
  )
}
